import { Link } from '../../types/Link.type'
import { CfgApiMenu } from '../../types/CfgApiMenu.type'
import { extractMenuItems } from './extractMenuItems'
import { MenuType, MENU_TYPES } from './menu-type.type'

export const fetchMenuData = async (
  menuType: MenuType
): Promise<CfgApiMenu | void> => {
  try {
    const loc = window.location?.href ? new URL(window.location.href) : null

    const isCmsDomain = loc?.hostname.includes('cms')
    const isAmeripriseDomain =
      loc?.hostname.endsWith('.ameriprise.com') ||
      loc?.hostname === 'ameriprise.com'
    const isLocalhost = loc?.hostname === 'localhost'
    const isAmeripriseAdvisorsDomain = loc?.hostname.includes(
      'ameripriseadvisors.com'
    )
    const isQAEnvironment = loc?.hostname.includes('qa.')

    let hostname
    let basePathName
    if (isCmsDomain) {
      basePathName = '/site/_cmsinternal/cfgapi/hst/menus/ampcom/'
    } else {
      basePathName = `/cfgapi/hst/menus/ampcom/`
    }

    if (isAmeripriseDomain || isLocalhost) {
      hostname = ''
    }

    if (isAmeripriseAdvisorsDomain) {
      if (isQAEnvironment) {
        if (loc?.hostname.startsWith('www3')) {
          hostname = 'https://www7.qa.ameriprise.com'
        } else {
          hostname = 'https://www.qa.ameriprise.com'
        }
      } else {
        hostname = 'https://www.ameriprise.com'
      }
    }

    if (!MENU_TYPES.includes(menuType)) {
      return
    }

    return fetch(`${hostname}${basePathName}${menuType}`, {
      mode: 'cors',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to fetch menu data')
        }
        return response.json()
      })
      .catch(() => {
        console.warn('Error occurred during fetch')
        return
      })
  } catch {
    console.warn('Invalid URL provided')
    return
  }
}

export const cacheMenuItems = (
  cacheId: string,
  menuItems: Link[],
  version: string
): void => {
  const cacheData = {
    version,
    menuItems
  }
  sessionStorage.setItem(cacheId, JSON.stringify(cacheData))
}

export const getCachedMenuItems = (
  cacheId: string,
  version: string
): Link[] | null => {
  const menuItemsCacheRaw = sessionStorage.getItem(cacheId)
  if (menuItemsCacheRaw) {
    const cacheData = JSON.parse(menuItemsCacheRaw)
    if (cacheData.version === version) {
      return cacheData.menuItems
    }
  }
  return null
}

// TODO add support to get menu while on CMS domain
export const getMenu = async (
  menuType: MenuType,
  depth: number,
  version: string
): Promise<Link[]> => {
  const cacheId = `${menuType}-cache`
  let menuItemsCache = getCachedMenuItems(cacheId, version)

  if (!menuItemsCache) {
    try {
      const body = await fetchMenuData(menuType)
      if (!body) {
        // Return empty array on error, do not leak details
        menuItemsCache = []
      } else {
        menuItemsCache = extractMenuItems(body, depth)
        cacheMenuItems(cacheId, menuItemsCache, version)
      }
    } catch {
      // Suppress error details
      menuItemsCache = []
    }
  }

  return menuItemsCache
}
